import { createFileRoute, useNavigate } from "@tanstack/react-router";
import React, { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  listSubmissions,
  submissionsStats,
  updateSubmission,
  getMyAdminStatus,
} from "@/lib/admin.functions";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({
    meta: [
      { title: "Submissions Admin - Tables and Bridges" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminPage,
});

type TriageStatus = "new" | "contacted" | "accepted" | "declined" | "archived";

type Submission = {
  id: string;
  created_at: string;
  form_type: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  kit_tag: string | null;
  kit_status: string;
  kit_subscriber_id: number | null;
  kit_error: string | null;
  status: TriageStatus;
  notes: string | null;
  payload: unknown;
  contacted_at: string | null;
  decided_at: string | null;
  decided_by: string | null;
};

const STATUS_STYLES: Record<TriageStatus, string> = {
  new: "bg-neutral-200 text-neutral-800",
  contacted: "bg-blue-100 text-blue-800",
  accepted: "bg-green-100 text-green-800",
  declined: "bg-red-100 text-red-800",
  archived: "bg-neutral-100 text-neutral-500",
};
const STATUS_ORDER: TriageStatus[] = ["new", "contacted", "accepted", "declined", "archived"];

function AdminPage() {
  const navigate = useNavigate();
  const list = useServerFn(listSubmissions);
  const stats = useServerFn(submissionsStats);
  const update = useServerFn(updateSubmission);
  const status = useServerFn(getMyAdminStatus);

  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [rows, setRows] = useState<Submission[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [search, setSearch] = useState("");
  const [formType, setFormType] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    status().then((s) => setIsAdmin(s.isAdmin));
  }, [status]);

  const load = useMemo(
    () => async () => {
      setLoading(true);
      try {
        const [r, s] = await Promise.all([
          list({
            data: {
              search: search || undefined,
              formType: formType || undefined,
              status: statusFilter || undefined,
              limit: 200,
            },
          }),
          stats(),
        ]);
        setRows(r.rows as Submission[]);
        setCounts(s.byType);
        setStatusCounts(s.byStatus);
      } finally {
        setLoading(false);
      }
    },
    [list, stats, search, formType, statusFilter],
  );

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  }

  function exportCsv() {
    const headers = [
      "created_at", "form_type", "email", "first_name", "last_name",
      "company", "kit_tag", "kit_status", "status", "notes",
    ];
    const escape = (v: unknown) => {
      const s = v == null ? "" : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const csv = [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => escape((r as unknown as Record<string, unknown>)[h])).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (isAdmin === null) {
    return <div className="p-8">Loading...</div>;
  }
  if (!isAdmin) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <h1 className="text-2xl font-bold mb-2">Access denied</h1>
        <p className="text-neutral-600 mb-4">
          Your account is signed in but does not have admin access. Contact info@tablesandbridges.com.
        </p>
        <button onClick={handleSignOut} className="text-tb-navy underline">Sign out</button>
      </div>
    );
  }

  const formTypes = Object.keys(counts).sort();

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-tb-navy text-white px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-serif font-bold">Submissions Admin</h1>
          <p className="text-sm text-white/70">Tables and Bridges</p>
        </div>
        <button onClick={handleSignOut} className="text-sm underline">Sign out</button>
      </header>

      <div className="p-6 max-w-7xl mx-auto space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {formTypes.map((ft) => (
            <div key={ft} className="bg-white rounded p-3 border">
              <div className="text-xs uppercase text-neutral-500">{ft}</div>
              <div className="text-xl font-bold text-tb-navy">{counts[ft]}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 bg-white p-3 rounded border">
          {STATUS_ORDER.map((st) => {
            const active = statusFilter === st;
            return (
              <button
                key={st}
                onClick={() => setStatusFilter(active ? "" : st)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                  active ? "ring-2 ring-tb-navy" : ""
                } ${STATUS_STYLES[st]}`}
              >
                {st} · {statusCounts[st] ?? 0}
              </button>
            );
          })}
          {statusFilter && (
            <button
              onClick={() => setStatusFilter("")}
              className="px-3 py-1 rounded-full text-xs underline text-neutral-600"
            >
              clear
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-2 items-center bg-white p-3 rounded border">
          <input
            placeholder="Search email, name, company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded px-3 py-2 flex-1 min-w-64"
          />
          <select
            value={formType}
            onChange={(e) => setFormType(e.target.value)}
            className="border rounded px-3 py-2"
          >
            <option value="">All form types</option>
            {formTypes.map((ft) => (
              <option key={ft} value={ft}>{ft}</option>
            ))}
          </select>
          <button
            onClick={() => void load()}
            className="bg-tb-navy text-white px-4 py-2 rounded"
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
          <button
            onClick={exportCsv}
            className="bg-tb-gold text-tb-navy px-4 py-2 rounded font-medium"
          >
            Export CSV ({rows.length})
          </button>
        </div>

        <div className="bg-white rounded border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-100 text-left">
              <tr>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Company</th>
                <th className="px-3 py-2">Kit</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Triage</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr className="border-t hover:bg-neutral-50">
                    <td className="px-3 py-2 whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">{r.form_type}</td>
                    <td className="px-3 py-2">
                      {[r.first_name, r.last_name].filter(Boolean).join(" ") || "-"}
                    </td>
                    <td className="px-3 py-2">{r.email || "-"}</td>
                    <td className="px-3 py-2">{r.company || "-"}</td>
                    <td className="px-3 py-2">
                      <span
                        className={
                          r.kit_status === "sent"
                            ? "text-green-700"
                            : r.kit_status === "error"
                              ? "text-red-700"
                              : "text-neutral-500"
                        }
                      >
                        {r.kit_status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mr-2 ${STATUS_STYLES[r.status]}`}>
                        {r.status}
                      </span>
                      <select
                        value={r.status}
                        onChange={async (e) => {
                          const next = e.target.value as TriageStatus;
                          const res = await update({ data: { id: r.id, status: next } });
                          const patch = (res?.row ?? { status: next }) as Partial<Submission>;
                          setRows((prev) =>
                            prev.map((x) => (x.id === r.id ? { ...x, ...patch } : x)),
                          );
                        }}
                        className="border rounded px-2 py-1 text-xs"
                      >
                        {STATUS_ORDER.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-xs text-neutral-600 whitespace-nowrap">
                      {r.contacted_at && (
                        <div>Contacted {new Date(r.contacted_at).toLocaleDateString()}</div>
                      )}
                      {r.decided_at && (
                        <div>
                          {r.status === "accepted" ? "Accepted" : r.status === "declined" ? "Declined" : "Decided"}{" "}
                          {new Date(r.decided_at).toLocaleDateString()}
                        </div>
                      )}
                      {!r.contacted_at && !r.decided_at && <span className="text-neutral-400">-</span>}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                        className="text-tb-navy underline text-xs"
                      >
                        {expanded === r.id ? "hide" : "view"}
                      </button>
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr className="bg-neutral-50 border-t">
                      <td colSpan={9} className="px-3 py-3">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <div className="text-xs font-semibold uppercase text-neutral-500 mb-1">Payload</div>
                            <pre className="text-xs bg-white p-2 rounded border overflow-auto max-h-64">
                              {JSON.stringify(r.payload, null, 2)}
                            </pre>
                            {r.kit_error && (
                              <div className="mt-2 text-xs text-red-700">
                                Kit error: {r.kit_error}
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="text-xs font-semibold uppercase text-neutral-500 mb-1">Notes</div>
                            <textarea
                              defaultValue={r.notes ?? ""}
                              onBlur={async (e) => {
                                const notes = e.target.value;
                                await update({ data: { id: r.id, notes } });
                                setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, notes } : x)));
                              }}
                              className="w-full border rounded p-2 text-sm h-32"
                              placeholder="Internal notes (saved on blur)"
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={9} className="px-3 py-8 text-center text-neutral-500">
                    No submissions found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
